// Position/size of the selected element: exact mm frame in a free section,
// width / spacing / placement in a flowing one. Also name, lock and hide, and
// moving the element to another section.

import React from "react";
import { Section, Field, NumberInput, Segmented, TextInput, Toggle, SelectInput } from "../controls";

export default function LayoutPanel({ el, band, design, update, onMoveToBand, contentWidth }) {
  const free = !!el.frame;
  const f = el.frame || {};
  const setFrame = (patch) => update((e) => ({ ...e, frame: { ...e.frame, ...patch } }), "frame");
  const otherBands = design.bands.filter((b) => b.id !== band.id && !(el.type === "row" && b.kind === "free"));

  return (
    <Section title="Layout">
      <Field label="Name (shown in Layers)">
        <TextInput value={el.name || ""} maxLength={60} placeholder="Optional" onChange={(v) => update({ name: v || undefined }, "name")} />
      </Field>
      <div className="td-row-gap">
        <Toggle label="Lock position" checked={el.locked} onChange={(v) => update({ locked: v || undefined })} />
        <Toggle label="Hide" checked={el.hidden} onChange={(v) => update({ hidden: v || undefined })} />
      </div>
      {free ? (
        <>
          <div className="td-grid2">
            <Field label="X">
              <NumberInput value={f.x} min={-50} max={500} step={0.5} unit="mm" onChange={(v) => setFrame({ x: v })} />
            </Field>
            <Field label="Y">
              <NumberInput value={f.y} min={-50} max={500} step={0.5} unit="mm" onChange={(v) => setFrame({ y: v })} />
            </Field>
            <Field label="Width">
              <NumberInput value={f.w} min={1} max={500} step={0.5} unit="mm" onChange={(v) => setFrame({ w: v })} />
            </Field>
            <Field label="Height">
              <NumberInput value={f.h} min={1} max={500} step={0.5} unit="mm" onChange={(v) => setFrame({ h: v })} />
            </Field>
          </div>
          <div className="td-grid2">
            <Field label="Rotate">
              <NumberInput value={f.rotate || 0} min={-180} max={180} step={1} unit="°" onChange={(v) => setFrame({ rotate: v || undefined })} />
            </Field>
            <Field label="Quick place">
              <SelectInput
                value=""
                placeholder="Choose…"
                options={[
                  { value: "left", label: "Left edge" },
                  { value: "center", label: "Centre horizontally" },
                  { value: "right", label: "Right edge" },
                  { value: "full", label: "Full width" },
                  { value: "top", label: "Top of section" },
                  { value: "middle", label: "Middle of section" },
                  { value: "bottom", label: "Bottom of section" },
                ]}
                onChange={(v) => {
                  const bh = band.height || 40;
                  const map = {
                    left: { x: 0 },
                    center: { x: Math.round(((contentWidth - f.w) / 2) * 10) / 10 },
                    right: { x: Math.round((contentWidth - f.w) * 10) / 10 },
                    full: { x: 0, w: Math.round(contentWidth * 10) / 10 },
                    top: { y: 0 },
                    middle: { y: Math.round(((bh - f.h) / 2) * 10) / 10 },
                    bottom: { y: Math.round((bh - f.h) * 10) / 10 },
                  };
                  if (map[v]) setFrame(map[v]);
                }}
              />
            </Field>
          </div>
        </>
      ) : (
        <>
          <div className="td-grid2">
            <Field label="Width">
              <NumberInput value={el.widthPct ?? 100} min={5} max={100} step={1} unit="%" onChange={(v) => update({ widthPct: v }, "width")} />
            </Field>
            <Field label="Space above">
              <NumberInput value={el.marginTop ?? 0} min={0} max={100} step={0.5} unit="mm" onChange={(v) => update({ marginTop: v }, "margin")} />
            </Field>
          </div>
          <Field label="Placement">
            <Segmented
              ariaLabel="Placement in the section"
              value={el.blockAlign || "left"}
              onChange={(v) => update({ blockAlign: v || "left" })}
              options={[
                { value: "left", label: "Left", icon: "align-left" },
                { value: "center", label: "Centre", icon: "align-center" },
                { value: "right", label: "Right", icon: "align-right" },
              ]}
            />
          </Field>
        </>
      )}
      {otherBands.length > 0 && (
        <Field label="Move to section">
          <SelectInput
            value=""
            placeholder="Choose a section…"
            options={otherBands.map((b) => ({ value: b.id, label: `${b.label} (${b.kind === "free" ? "free" : "flowing"})` }))}
            onChange={(v) => v && onMoveToBand(v)}
          />
        </Field>
      )}
    </Section>
  );
}
